import { capitalize, invariant } from "@banjoanton/utils";
import { ChessGameRepository } from "@chesspresso/chess/src/db/chess-game-repository";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

type BreadcrumbItem = {
    title: string;
    link: string;
};

const routeMapping: Record<string, BreadcrumbItem[]> = {
    "/dashboard": [{ title: "Dashboard", link: "/dashboard" }],
    "/dashboard/overview": [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Overview", link: "/dashboard/overview" },
    ],
};

const getTitleFromIndexedDB = async (slug: string): Promise<string> => {
    const game = await ChessGameRepository.getBySlug(slug);
    invariant(game, `Game not found for slug: ${slug}`);
    return `${capitalize(game.white.username)} vs ${capitalize(game.black.username)} (${new Date(game.endTimeUnix * 1000).toLocaleDateString()})`;
};

export const useBreadcrumbs = (): BreadcrumbItem[] => {
    const { pathname } = useLocation();
    const [analyzeTitle, setAnalyzeTitle] = useState<string>();

    useEffect(() => {
        const match = pathname.match(/^\/analyze\/([^/]+)$/);
        if (match) {
            const slug = match[1];
            invariant(slug, "Slug must be defined for analyze route");
            getTitleFromIndexedDB(slug).then((title) => setAnalyzeTitle(title));
        } else {
            setAnalyzeTitle("");
        }
    }, [pathname]);

    const dynamicBreadcrumbs = useMemo(() => {
        if (routeMapping[pathname]) {
            return routeMapping[pathname];
        }

        const segments = pathname.split("/").filter(Boolean);
        return segments.map((segment, index) => {
            const path = `/${segments.slice(0, index + 1).join("/")}`;
            let title = segment.charAt(0).toUpperCase() + segment.slice(1);

            // Replace slug title for /analyze/<slug>
            if (segments[0] === "analyze" && index === 1 && analyzeTitle) {
                title = analyzeTitle;
            }
            return {
                title,
                link: path,
            };
        });
    }, [pathname, analyzeTitle]);

    const allBreadcrumbs: BreadcrumbItem[] = [
        { title: "Chesspresso", link: "/" },
        ...dynamicBreadcrumbs,
    ];

    return allBreadcrumbs;
};
